/**
 * Tenant branding (app name + org name) — one query, cached, used by the
 * shell, login pages and document.title. Public endpoint by design (the login
 * page itself is branded), so this works signed-out too.
 */
import { useQuery } from '@tanstack/react-query';
import { qk } from './queryClient';
import { useEffect, useMemo } from 'react';
import { titleCase, vocab, type VocabularyKey, type VocabularyOverrides } from '@itsm/shared';

export interface Branding {
  appName: string;
  name: string;
  logoPath: string | null;
  logoDarkPath: string | null;
  faviconPath: string | null;
  brandColor: string | null;
  radius?: string | null;
  version?: number;
  /** Step 79 — the tenant's noun overrides, sparse. */
  vocabulary?: VocabularyOverrides;
}

export function useBranding(): Branding | null {
  const { data } = useQuery({
    queryKey: qk.branding,
    queryFn: async () => {
      // Bare fetch on purpose: public endpoint, used pre-auth on login pages.
      const res = await fetch('/api/tenant/branding');
      if (!res.ok) return null;
      return (await res.json()) as Branding;
    },
    staleTime: 5 * 60_000,
  });
  return data ?? null;
}

/** Keep the browser tab honest about the product's configured name. */
export function useDocumentTitle(suffix?: string) {
  const branding = useBranding();
  useEffect(() => {
    if (!branding?.appName) return;
    document.title = suffix ? `${suffix} — ${branding.appName}` : branding.appName;
  }, [branding?.appName, suffix]);
}

/**
 * Keep the browser tab's ICON honest, and bust a stale cache — Step 184.
 *
 * The `<link rel="icon">` in index.html points at `/api/tenant/favicon` with no
 * version. When a desk first loaded before it had set a favicon, the browser
 * cached that 204 "no icon" against that exact URL and never asked again — so a
 * favicon uploaded later never appeared (Kai: *"there's no favicon for the
 * site"*). Rewriting the href with the branding VERSION gives the browser a URL
 * it has not cached, which is the only reliable way to move a favicon a browser
 * has already made up its mind about. It also updates apple-touch-icon the same
 * way, so the home-screen icon tracks a rebrand.
 */
export function useFavicon() {
  const branding = useBranding();
  const version = branding?.version ?? 0;
  const hasIcon = Boolean(branding?.faviconPath);
  useEffect(() => {
    if (!branding) return;
    // A URL the browser has not seen: the version changes whenever the favicon
    // does, and `?v=` reaches the same endpoint, which serves the icon or 204s.
    const href = hasIcon ? `/api/tenant/favicon?v=${version}` : '/icon.svg';
    for (const rel of ['icon', 'apple-touch-icon']) {
      let link = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
      if (!link) {
        link = document.createElement('link');
        link.rel = rel;
        document.head.appendChild(link);
      }
      link.href = href;
    }
  }, [branding, hasIcon, version]);
}

/**
 * The white-label vocabulary — Step 79.
 *
 * One helper, so a rename reaches every surface at once. Reads from the same
 * cached branding query the app name uses: the nouns and the product name
 * answer the same question ("whose product is this?") and asking twice would
 * let them arrive at different moments and flicker.
 *
 * Signed-out and pre-load both resolve to the defaults rather than blanks —
 * a login page that renders "Sign in to raise a" while a fetch settles is
 * worse than one that says "ticket" and is briefly wrong for a tenant who
 * renamed it.
 *
 * Usage: `const t = useVocab(); t('ticket').one` · `t.title('ticket')`
 */
export interface Vocab {
  (key: VocabularyKey): { one: string; many: string };
  /** Sentence/heading case of the singular. */
  title: (key: VocabularyKey) => string;
  /** Sentence/heading case of the plural — page headings, nav labels. */
  titleMany: (key: VocabularyKey) => string;
}

export function useVocab(): Vocab {
  const branding = useBranding();
  const overrides = branding?.vocabulary;
  return useMemo(() => {
    const fn = ((key: VocabularyKey) => vocab(overrides, key)) as Vocab;
    fn.title = (key) => titleCase(vocab(overrides, key).one);
    fn.titleMany = (key) => titleCase(vocab(overrides, key).many);
    return fn;
  }, [overrides]);
}
